import { useEffect, useMemo, useState } from 'react';
import { ContactDetailModal } from '../components/contacts/ContactDetailModal';
import { ContactFormModal } from '../components/contacts/ContactFormModal';
import { ContactListRow } from '../components/contacts/ContactListRow';
import { PageHeader } from '../components/ui/PageHeader';
import { PageToolbar } from '../components/ui/PageToolbar';
import { useCrmData } from '../contexts/CrmDataContext';
import { usePortal } from '../contexts/PortalContext';
import { canCreatePersonalCrmRecords } from '../lib/roleAccess';
import type { Contact } from '../types/crm';
import { contactLabel, userCanWriteOwnedRecord } from '../utils/crmHelpers';
import {
  contactSearchPool,
  dedupeContactsForList,
  buildContactDedupKey,
  salespersonCodesForContactAccounts,
  type DedupedContactListEntry,
} from '../utils/contactDedup';

type ContactSortKey = 'contact' | 'account' | 'reach' | 'salesperson';
type ContactSortDirection = 'asc' | 'desc';

const SORT_COLUMNS_CRM: { key: ContactSortKey; label: string }[] = [
  { key: 'contact', label: 'Contact' },
  { key: 'account', label: 'Account' },
  { key: 'reach', label: 'Email / Phone' },
  { key: 'salesperson', label: 'Salesperson' },
];

const SORT_COLUMNS_IT: { key: ContactSortKey; label: string }[] = [
  { key: 'contact', label: 'Contact' },
  { key: 'account', label: 'Account' },
  { key: 'reach', label: 'Email / Phone' },
];

function contactSortValue(entry: DedupedContactListEntry, key: ContactSortKey): string {
  switch (key) {
    case 'contact':
      return contactLabel(entry.contact);
    case 'account':
      return entry.accounts
        .map((account) => account.company_name?.trim() || account.code?.trim() || '')
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    case 'reach':
      return (
        entry.contact.email?.trim().toLowerCase() ||
        entry.contact.phone?.trim().toLowerCase() ||
        ''
      );
    case 'salesperson':
      return salespersonCodesForContactAccounts(entry.accounts).toLowerCase();
  }
}

export function ContactsPage() {
  const { kind: portalKind } = usePortal();
  const isItPortal = portalKind === 'it';
  const {
    data,
    getMyData,
    loading,
    refresh,
    effectiveRole,
    username,
    supervisorTeam,
    actorSalespersonCodes,
    departmentSlug,
  } = useCrmData();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<ContactSortKey>('contact');
  const [sortDir, setSortDir] = useState<ContactSortDirection>('asc');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [detailEntry, setDetailEntry] = useState<DedupedContactListEntry | null>(null);

  const canCreateContact = canCreatePersonalCrmRecords(effectiveRole);
  const sortColumns = isItPortal ? SORT_COLUMNS_IT : SORT_COLUMNS_CRM;

  useEffect(() => {
    if (isItPortal && sortKey === 'salesperson') {
      setSortKey('contact');
      setSortDir('asc');
    }
  }, [isItPortal, sortKey]);

  function canEditContact(contact: Contact): boolean {
    return userCanWriteOwnedRecord(
      contact.owner_id,
      effectiveRole,
      username,
      data.users,
      supervisorTeam,
      {
        departmentSlug,
        clientId: contact.client_id,
        clients: data.clients,
        actorSalespersonCodes,
        userSalespersonScope: true,
      },
    );
  }

  const contacts = useMemo(() => {
    let list = dedupeContactsForList([...getMyData('contacts')], data.clients);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((entry) =>
        contactSearchPool(
          entry,
          isItPortal ? '' : salespersonCodesForContactAccounts(entry.accounts),
          { includeSalesperson: !isItPortal },
        ).includes(q),
      );
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const left = contactSortValue(a, sortKey);
      const right = contactSortValue(b, sortKey);
      const cmp = left.localeCompare(right, undefined, { sensitivity: 'base' });
      if (cmp !== 0) return cmp * dir;
      return contactLabel(a.contact).localeCompare(contactLabel(b.contact)) * dir;
    });
  }, [getMyData, sortKey, sortDir, search, data.clients, isItPortal]);

  function toggleSort(key: ContactSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  }

  const detailForModal = detailEntry
    ? {
        contact: detailEntry.contact,
        brandNames: detailEntry.accounts.map((a) => a.company_name).filter(Boolean),
        relatedContacts: detailEntry.relatedContacts,
      }
    : null;

  return (
    <div className="contacts-page">
      <PageHeader
        title="Contacts"
        description="View and manage contacts linked to customer accounts."
        actions={
          canCreateContact ? (
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              + New
            </button>
          ) : undefined
        }
      />

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={
          isItPortal
            ? 'Search by name, email or account'
            : 'Search by name, email, account or salesperson'
        }
        action={
          <div className="contacts-page__toolbar-aside">
            <span className="page-count">
              {loading ? 'Loading…' : `${contacts.length} contact${contacts.length === 1 ? '' : 's'}`}
            </span>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => void refresh()}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
        }
      />

      {loading && contacts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading contacts…</p>
        </div>
      ) : contacts.length === 0 ? (
        <div className="empty-state">
          <p>No contacts match your filters.</p>
        </div>
      ) : (
        <div className={`contact-list card${isItPortal ? ' contact-list--no-salesperson' : ''}`}>
          <div className="contact-list__head" role="row">
            <span />
            {sortColumns.map(({ key, label }) => {
              const active = sortKey === key;
              const ariaSort = active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
              return (
                <button
                  key={key}
                  type="button"
                  className={`contact-list__sort-btn${active ? ' is-active' : ''}`}
                  onClick={() => toggleSort(key)}
                  aria-sort={ariaSort}
                >
                  <span>{label}</span>
                  {active ? (
                    <span className="contact-list__sort-ind" aria-hidden>
                      {sortDir === 'asc' ? '▲' : '▼'}
                    </span>
                  ) : null}
                </button>
              );
            })}
            <span />
          </div>
          {contacts.map((entry) => (
            <ContactListRow
              key={buildContactDedupKey(entry.contact)}
              contact={entry.contact}
              accounts={entry.accounts}
              allClients={data.clients}
              salespersonCode={
                isItPortal ? '' : salespersonCodesForContactAccounts(entry.accounts)
              }
              hideSalesperson={isItPortal}
              onOpen={() => setDetailEntry(entry)}
            />
          ))}
        </div>
      )}

      <ContactDetailModal
        open={detailEntry != null}
        entry={detailForModal}
        accounts={detailEntry?.accounts}
        salespersonCode={
          isItPortal
            ? undefined
            : detailEntry
              ? salespersonCodesForContactAccounts(detailEntry.accounts)
              : undefined
        }
        hideSalesperson={isItPortal}
        canEdit={detailEntry ? canEditContact(detailEntry.contact) : false}
        onClose={() => setDetailEntry(null)}
        onEdit={(contact) => {
          setEditing(contact);
          setModalOpen(true);
        }}
      />

      <ContactFormModal
        open={modalOpen}
        contact={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
}
